import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BookProjectApiService } from '../../core/services/book-project-api.service';
import { BookCoverApiService } from '../../core/services/book-cover-api.service';
import { SharedBookProjectDto } from '../../core/models/book-project.model';
import { PagedResponse } from '../../core/models/paged-response.model';
import { Meta, Title } from "@angular/platform-browser";

@Component({
  selector: 'app-discover',
  templateUrl: './discover.component.html',
  styleUrls: ['./discover.component.scss']
})
export class DiscoverComponent implements OnInit {
  publicBooks: SharedBookProjectDto[] = [];
  bookCovers: { [id: string]: string } = {};
  coverLoading: { [id: string]: boolean } = {};
  currentPage: number = 1;
  pageSize: number = 12;
  totalItems: number = 0;
  totalPages: number = 0;
  pages: number[] = [];
  private placeholderCount = 12;
  private usedPlaceholders: number[] = [];

  constructor(
    private meta: Meta,
    private title: Title,
    private bookProjectApiService: BookProjectApiService,
    private bookCoverApiService: BookCoverApiService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    this.title.setTitle('Discover Books - Talespinner.io | AI-Powered Storytelling Platform');
    this.meta.updateTag({ name: 'description', content: 'Discover new stories and writing projects on Talespinner.io. Explore public books created by our community of writers and storytellers.' });
    this.meta.updateTag({ name: 'keywords', content: 'Talespinner discover books, public writing projects, community stories, AI-powered storytelling, creative writing platform' });

    this.route.queryParams.subscribe(params => {
      this.currentPage = parseInt(params['page']) || 1;
      this.loadPublicBooks();
    });
  }

  loadPublicBooks() {
    this.bookProjectApiService.getPublicBooks(this.currentPage, this.pageSize).subscribe(
      (response: PagedResponse<SharedBookProjectDto>) => {
        console.log('Public books:', response);

        this.publicBooks = response.data;
        this.totalItems = response.totalCount;
        this.totalPages = Math.ceil(this.totalItems / this.pageSize);
        this.updatePagination();
        this.resetPlaceholders();
        this.loadBookCovers();
      },
      (error) => {
        console.error('Error loading public books:', error);
      }
    );
  }

  updatePagination() {
    const maxPagesToShow = 5;
    const halfMaxPages = Math.floor(maxPagesToShow / 2);
    let startPage = Math.max(this.currentPage - halfMaxPages, 1);
    let endPage = Math.min(startPage + maxPagesToShow - 1, this.totalPages);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(endPage - maxPagesToShow + 1, 1);
    }

    this.pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  loadBookCovers() {
    this.publicBooks.forEach(book => {
      this.coverLoading[book.id] = true;
      this.bookCoverApiService.getBookCover(book.id).subscribe(
        (coverBlob) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            this.bookCovers[book.id] = reader.result as string;
            this.coverLoading[book.id] = false;
          };
          reader.readAsDataURL(coverBlob);
        },
        (error) => {
          this.useplaceholder(book.id);
        }
      );
    });
  }

  private useplaceholder(bookId: string) {
    this.bookCovers[bookId] = this.getPlaceholder();
    this.coverLoading[bookId] = false;
  }

  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { page: page },
        queryParamsHandling: 'merge'
      });
    }
  }

  private getPlaceholder(): string {
    let index: number;
    if (this.usedPlaceholders.length < this.placeholderCount) {
      do {
        index = Math.floor(Math.random() * this.placeholderCount) + 1;
      } while (this.usedPlaceholders.includes(index));
      this.usedPlaceholders.push(index);
    } else {
      index = Math.floor(Math.random() * this.placeholderCount) + 1;
    }
    return `assets/images/book-cover-placeholder${index}.webp`;
  }

  private resetPlaceholders() {
    this.usedPlaceholders = [];
    this.bookCovers = {};
    this.coverLoading = {};
  }

  protected readonly Math = Math;
}
